import React, { useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { mapLanguageToComponentProps } from 'i18n'
import { logEvent } from 'actions/audit-trail'
import grin from 'assets/icons/grin.svg'
import frown from 'assets/icons/frown.svg'
import smile from 'assets/icons/smile.svg'
import angry from 'assets/icons/angry.svg'
import { Subtitle, Body } from 'components/styled/typography'
import { Flex } from 'components/styled/containers/flex'
import { Box } from 'components/styled/containers/box'
import { I18nComponentType } from 'i18n/i18n-types'
import * as analytics from 'analytics/performance'

interface FeedbackPulseProps {
  i18n: I18nComponentType
  dispatch: any
  position: { _id: string }
  company: { _id: string }
  children?: React.ReactNode | React.ReactNode[]
  context?: string
  isPreview?: boolean
}

const RatingIconImage = styled.img`
  width: ${({ theme }) => theme.space.s40};
  height: ${({ theme }) => theme.space.s40};
  margin-left: ${({ theme }) => theme.space.s12};
  margin-right: ${({ theme }) => theme.space.s12};
  vertical-align: middle;
  display: inline-block;
  transform: translateZ(0);
  backface-visibility: hidden;
  transition-duration: 0.35s;
  transition-property: filter, transform;
  transition-timing-function: cubic-bezier(0.175, 0.885, 0.25, 1.5);

  &:hover,
  &:focus,
  &:active {
    transform: scale(1.16);
  }
`

const FeedbackPulse = ({
  i18n,
  dispatch,
  position,
  company,
  children,
  context = 'interview',
  isPreview = false,
}: FeedbackPulseProps) => {
  const [voted, setVoted] = useState<boolean>(false)

  const title =
    context === 'interview'
      ? i18n.feedbackPulse.howWouldYouRateInterview
      : i18n.feedbackPulse.howWouldYouRateJobAd

  const onGiveFeedback = (rating: number) => {
    if (isPreview) {
      setVoted(true)
      return
    }

    if (!voted) {
      dispatch(
        logEvent({
          ec: company._id,
          ea: 'pulse-feedback',
          el: position._id,
          ev: rating,
        })
      )
      setVoted(true)
    }

    const positionProps =
      context === 'interview'
        ? {
            interviewId: position._id,
          }
        : {
            jobAdId: position._id,
          }

    analytics.onClickEvent({
      locationId: context === 'interview' ? 'interviewPage' : 'jobAdPage',
      id: 'feedbackPulseButton',
      properties: {
        ...positionProps,
        companyId: company?._id,
      },
    })
  }

  return (
    <Flex flexDirection="column" mt="s32" alignItems="center">
      <div className="feedback-pulse-options">
        {!voted ? (
          <>
            <Subtitle textAlign="center">{title}</Subtitle>
            <Body textAlign="center">
              {i18n.feedbackPulse.yourResponseIsAnonymous}
            </Body>
            <Flex mt="s32" justifyContent="center">
              <button
                type="button"
                onClick={() => onGiveFeedback(4)}
                tabIndex={0}
              >
                <RatingIconImage
                  src={grin}
                  alt="Very good"
                  className="rating-icon"
                />
              </button>
              <button
                type="button"
                onClick={() => onGiveFeedback(3)}
                tabIndex={0}
              >
                <RatingIconImage
                  src={smile}
                  alt="Good"
                  className="rating-icon"
                />
              </button>
              <button
                type="button"
                onClick={() => onGiveFeedback(2)}
                tabIndex={0}
              >
                <RatingIconImage
                  src={frown}
                  alt="Bad"
                  className="rating-icon"
                />
              </button>
              <button
                type="button"
                onClick={() => onGiveFeedback(1)}
                tabIndex={0}
              >
                <RatingIconImage
                  src={angry}
                  alt="Very bad"
                  className="rating-icon"
                />
              </button>
            </Flex>
          </>
        ) : (
          <Box mt="s8">
            <Subtitle textAlign="center">
              {i18n.feedbackPulse.thankYouFeedback}
            </Subtitle>
            {children}
          </Box>
        )}
      </div>
    </Flex>
  )
}

export default connect(mapLanguageToComponentProps())(FeedbackPulse)
